import React from 'react';
import { Box, Button, Container, Grid, List, ListItem, ListItemIcon, ListItemText, Paper, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import SmsIcon from '@mui/icons-material/Sms';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { PHONE_DISPLAY, PHONE_NUMBER, TEXT_NUMBER } from '../../utils/site';

// Only list what Diego has confirmed (see marketing/open-questions.md)
const itemsWeHaul = [
  'Couches, chairs, tables, dressers and other furniture',
  'Washers, dryers, stoves and other large appliances',
  'TVs and most electronics (recycled, as Wisconsin requires)',
  'Garage, basement, attic and shed clutter',
  'Yard waste and brush',
  'Construction and remodeling debris',
  'Office and business junk',
];

const steps = [
  {
    title: 'Tell us what needs to go',
    text: TEXT_NUMBER
      ? 'Call, text us a few photos, or send the quote form.'
      : 'Call us or send the quote form with a quick description.',
  },
  {
    title: 'Get a free quote',
    text: 'Pricing is based on how much space it takes up, how heavy it is, and what it costs to dispose of or recycle.',
  },
  {
    title: 'We do the lifting',
    text: 'We carry everything out, load it into our trailer and haul it away.',
  },
  {
    title: 'Donate, recycle, dispose',
    text: 'Usable items go to local charities when possible, recyclables get sorted, and the rest is disposed of properly.',
  },
];

const whiteOutline = { color: 'white', borderColor: 'rgba(255,255,255,0.7)', '&:hover': { borderColor: 'white' } };

// What we haul, how it works, and call/quote buttons, shared by the junk removal pages
const JunkRemovalInfo: React.FC = () => (
  <Box component="section" py={8} sx={{ backgroundColor: 'grey.50' }}>
    <Container maxWidth="lg">
      <Grid container spacing={6}>
        <Grid item xs={12} md={6}>
          <Typography variant="h3" component="h2" gutterBottom>
            What we haul
          </Typography>
          <List>
            {itemsWeHaul.map((item) => (
              <ListItem key={item} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          <Typography variant="body2" color="text.secondary">
            We can't take hazardous materials like chemicals, paint or solvents.
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h3" component="h2" gutterBottom>
            How it works
          </Typography>
          {steps.map((step, index) => (
            <Box key={step.title} display="flex" alignItems="flex-start" mb={3}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  mr: 2,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                }}
              >
                {index + 1}
              </Box>
              <Box>
                <Typography variant="h6" component="h3">
                  {step.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {step.text}
                </Typography>
              </Box>
            </Box>
          ))}
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{ mt: 6, p: { xs: 3, md: 4 }, textAlign: 'center', bgcolor: 'secondary.main', color: 'white', borderRadius: 2 }}
      >
        <Typography variant="h5" component="p" fontWeight={600} gutterBottom>
          Junk removal starts at $100
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
          Tell us what needs to go and we'll give you a free quote.
        </Typography>
        <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
          <Button variant="contained" color="primary" size="large" startIcon={<PhoneIcon />} href={`tel:${PHONE_NUMBER}`}>
            Call {PHONE_DISPLAY}
          </Button>
          {TEXT_NUMBER && (
            <Button variant="outlined" size="large" startIcon={<SmsIcon />} href={`sms:${TEXT_NUMBER}`} sx={whiteOutline}>
              Text us photos
            </Button>
          )}
          <Button variant="outlined" size="large" startIcon={<RequestQuoteIcon />} href="#quote" sx={whiteOutline}>
            Get a free quote
          </Button>
        </Box>
      </Paper>
    </Container>
  </Box>
);

export default JunkRemovalInfo;
